import React, { useState, useMemo } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from './ui/badge';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_84e14be8-c66b-4e50-b378-639bfe417cdf/artifacts/vn03bb90_297950681_1043688306290539_3612407571660205954_n.jpg';

export const VisualCalendar = ({ outboundDates = [], returnDates = [], savingsPercentage }) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());

  const datesMap = useMemo(() => {
    const map = {};
    
    // Marcar datas de ida
    outboundDates.forEach(date => {
      const dateKey = new Date(date).toISOString().split('T')[0];
      map[dateKey] = { ...map[dateKey], isOutbound: true };
    });
    
    // Marcar datas de volta
    returnDates.forEach(date => {
      const dateKey = new Date(date).toISOString().split('T')[0];
      map[dateKey] = { ...map[dateKey], isReturn: true };
    });
    
    return map;
  }, [outboundDates, returnDates]);

  const getColorClass = (dateInfo) => {
    if (!dateInfo) return 'bg-slate-50 text-slate-400';
    
    // Se tem ida e volta no mesmo dia
    if (dateInfo.isOutbound && dateInfo.isReturn) {
      return 'bg-gradient-to-br from-blue-500 to-green-500 text-white font-semibold';
    }
    // Apenas ida
    if (dateInfo.isOutbound) {
      return 'bg-blue-500 text-white font-semibold hover:bg-blue-600';
    }
    // Apenas volta
    if (dateInfo.isReturn) {
      return 'bg-green-500 text-white font-semibold hover:bg-green-600';
    }
    
    return 'bg-slate-50 text-slate-400';
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(day);
    }
    return days;
  };

  const nextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1));
  };

  const prevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1));
  };

  const getDateInfo = (day) => {
    if (!day) return null;
    const dateKey = `${currentMonth.getFullYear()}-${String(currentMonth.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return datesMap[dateKey];
  };

  const days = getDaysInMonth(currentMonth);
  const weekDays = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
  const monthName = currentMonth.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });

  return (
    <div className="bg-white rounded-2xl p-6 shadow-lg" data-testid="visual-calendar">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <img 
            src={LOGO_URL} 
            alt="Rota Continental" 
            className="h-8 w-8 rounded-lg object-cover"
          />
          <h3 className="text-xl font-bold text-slate-900 capitalize">{monthName}</h3>
        </div>
        <div className="flex gap-2">
          <button
            onClick={prevMonth}
            className="p-2 rounded-full border border-slate-300 hover:bg-slate-100 transition"
            data-testid="prev-month-button"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button
            onClick={nextMonth}
            className="p-2 rounded-full border border-slate-300 hover:bg-slate-100 transition"
            data-testid="next-month-button"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="mb-4">
        <div className="grid grid-cols-7 gap-2 mb-2">
          {weekDays.map((day) => (
            <div key={day} className="text-center text-xs font-semibold text-slate-600 py-2">
              {day}
            </div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-2">
          {days.map((day, index) => {
            const dateInfo = getDateInfo(day);
            const colorClass = getColorClass(dateInfo);
            const cellKey = day ? `day-${day}` : `empty-${index}`;
            
            return (
              <div
                key={cellKey}
                className={`
                  aspect-square flex items-center justify-center rounded-lg transition-all cursor-default text-sm font-medium
                  ${day ? colorClass : ''}
                  ${!day ? 'invisible' : ''}
                `}
                data-testid={day && dateInfo ? `available-date-${day}` : undefined}
              >
                {day}
              </div>
            );
          })}
        </div>
      </div>

      <div className="mt-6 pt-4 border-t border-slate-200">
        <div className="flex items-center justify-center gap-6 text-sm flex-wrap">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded bg-blue-500"></div>
            <span className="text-slate-600">Datas de Ida</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded bg-green-500"></div>
            <span className="text-slate-600">Datas de Volta</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded bg-gradient-to-br from-blue-500 to-green-500"></div>
            <span className="text-slate-600">Ambas</span>
          </div>
        </div>
      </div>
    </div>
  );
};